// Patient app-flag override system used by NewPatientDialog (and, in the
// future, EditPatientDialog).
//
// Each entry below describes one overridable flag with:
//  - the key the server expects in `flags_raw`
//  - the rendering shape (boolean tri-state, primary-macro enum,
//    multi-select nutrient list, or numeric "hide after N hours")
//  - which clinician-level flag gates whether the row is visible at all
//  - the image asset shown beside the form when the row is hovered or
//    its value changes
//
// Mirrors v1's `addUser.tsx` behavior. Definitions live as plain data so
// the React component can stay a straightforward map over the array.

import macroSummaryHide from '@/assets/patient-flags/macro-summary-hide.png';
import macroSummaryShowTotalCarbs from '@/assets/patient-flags/macro-summary-show-total-carbs.png';
import macroSummaryShowNetCarbs from '@/assets/patient-flags/macro-summary-show.png';
import dietScoreShow from '@/assets/patient-flags/diet-score-show.png';
import nutritionInsightsShow from '@/assets/patient-flags/nutrition-insights-show.png';
import nutritionInsightsHide from '@/assets/patient-flags/nutrition-insights-hide.png';
import mindfulEatingHide from '@/assets/patient-flags/mindful-eating-hide.png';
import mindfulEatingShow from '@/assets/patient-flags/mindful-eating-show.png';
import coachingTabHide from '@/assets/patient-flags/coaching-tab-hide.png';
import coachingTabShow from '@/assets/patient-flags/coaching-tab-show.png';
import hideMealAfterShow from '@/assets/patient-flags/hide-meal-after-n-hours.png';
import primaryMacroNone from '@/assets/patient-flags/primary-macro-none.png';
import primaryMacroCals from '@/assets/patient-flags/primary-macro-cals.png';
import primaryMacroCarbs from '@/assets/patient-flags/primary-macro-carbs.png';
import primaryMacroTotalCarbs from '@/assets/patient-flags/primary-macro-total-carbs.png';
import primaryMacroProtein from '@/assets/patient-flags/primary-macro-protein.png';
import primaryMacroFat from '@/assets/patient-flags/primary-macro-fat.png';
import primaryMacroFibre from '@/assets/patient-flags/primary-macro-fibre.png';
import settingsPrimaryMacroEdit from '@/assets/patient-flags/settings-primary-maco-edit.png';
import frame from '@/assets/patient-flags/frame.png';

export const FRAME_IMAGE = frame;

/**
 * The set of keys we manage in the form. `flags_raw` accepts any of these
 * (and many more), but we explicitly enumerate to keep the form scope
 * predictable and the type narrow.
 */
export const PATIENT_FLAG_KEYS = [
  'patient_app_primary_macronutrient',
  'patient_app_primary_macronutrient_can_edit',
  'patient_show_total_carbs',
  'patient_app_show_diet_score',
  'patient_app_show_macro_nutrition_card',
  'patient_app_show_nutrition_data_and_insights',
  'patient_app_coaching_tab',
  'patient_app_mindful_eating_survey',
  'patient_app_key_nutrients',
  'patient_app_hide_meal_after_n_hours',
] as const;

export type PatientFlagKey = (typeof PATIENT_FLAG_KEYS)[number];

/**
 * Patient flag values can be:
 *  - boolean (most toggles)
 *  - string ('default_carbs_g', 'energy_kcal', etc. for primary macro)
 *  - undefined (means "use clinic default")
 *
 * The form stores everything as a `string | undefined` keyed by flag, and
 * we coerce to the right shape at submit time.
 */
export type PatientFlagsState = Partial<Record<PatientFlagKey, string | boolean | number | undefined>>;

/**
 * Clinic-level flag that gates whether a patient-level flag is visible at
 * all in the form. v1 uses these to keep the panel from showing settings
 * that the clinic doesn't permit clinicians to override per patient.
 */
type ClinicianGateFlag =
  | 'clinician_patient_allow_cal_carb_toggle'
  | 'clinician_patient_allow_macro_card_toggle'
  | 'clinician_patient_allow_patient_app_coaching_tab_toggle'
  | 'clinician_patient_allow_mindful_eating_survey_toggle'
  | null;

/**
 * Image to show in the preview panel given the resolved flag value (after
 * patient override → clinic default → unset). Pure functions so the form
 * can compute the "currently showing" image without state.
 */
type ImageResolver = (resolvedValue: unknown, ctx: { showTotalCarbs: boolean }) => string;

export type FlagControl =
  | { kind: 'boolean'; offLabel: string; onLabel: string }
  | { kind: 'primaryMacro' }
  | { kind: 'hideMealHours' }
  | { kind: 'keyNutrients' };

export type PatientFlagDef = {
  key: PatientFlagKey;
  label: string;
  description?: string;
  control: FlagControl;
  /** Clinic-level flag gating visibility; null = always visible. */
  visibleWhen: ClinicianGateFlag;
  /** Whether this row is hidden when "Safe mode" is on. */
  hideInSafeMode: boolean;
  /** Resolves the preview image for a given current value. */
  image: ImageResolver;
};

const showOrHide = (showImg: string, hideImg: string): ImageResolver => (v) =>
  v === true || v === 'true' ? showImg : hideImg;

export const PATIENT_FLAG_DEFS: PatientFlagDef[] = [
  {
    key: 'patient_app_primary_macronutrient',
    label: 'Primary in-app macronutrient display',
    description:
      'This macronutrient will be displayed prominently beside each meal logged in the RxFood app.',
    control: { kind: 'primaryMacro' },
    visibleWhen: 'clinician_patient_allow_cal_carb_toggle',
    hideInSafeMode: true,
    image: (v, ctx) => {
      if (!v || v === 'none') return primaryMacroNone;
      if (v === 'energy_kcal') return primaryMacroCals;
      if (v === 'protein_g') return primaryMacroProtein;
      if (v === 'fat_g') return primaryMacroFat;
      if (v === 'fiber_g') return primaryMacroFibre;
      if (v === 'default_carbs_g') return ctx.showTotalCarbs ? primaryMacroTotalCarbs : primaryMacroCarbs;
      return primaryMacroNone;
    },
  },
  {
    key: 'patient_app_primary_macronutrient_can_edit',
    label: 'Modify primary in-app macronutrient display',
    description:
      'In settings under "App settings", patients have the option to modify their primary in-app macronutrient.',
    control: {
      kind: 'boolean',
      onLabel: 'Allow patients to modify their primary in-app macronutrient',
      offLabel: 'Disallow patients from modifying their primary in-app macronutrient',
    },
    visibleWhen: 'clinician_patient_allow_cal_carb_toggle',
    hideInSafeMode: true,
    image: () => settingsPrimaryMacroEdit,
  },
  {
    key: 'patient_app_hide_meal_after_n_hours',
    label: 'Automatically hide meals after specified time',
    description:
      'Hide meals from view after a certain number of hours have passed since the meal was logged.',
    control: { kind: 'hideMealHours' },
    visibleWhen: null,
    hideInSafeMode: false,
    image: (v) => (Number(v) > 0 ? hideMealAfterShow : primaryMacroNone),
  },
  {
    key: 'patient_app_show_macro_nutrition_card',
    label: 'Macronutrient summary display',
    description:
      'A macronutrient summary (protein, carbs, fat, calories etc.) by meal and day will be displayed in the RxFood app.',
    control: { kind: 'boolean', onLabel: 'Show macronutrient summary', offLabel: 'Hide macronutrient summary' },
    visibleWhen: 'clinician_patient_allow_macro_card_toggle',
    hideInSafeMode: true,
    image: (v, ctx) =>
      v === true || v === 'true'
        ? ctx.showTotalCarbs
          ? macroSummaryShowTotalCarbs
          : macroSummaryShowNetCarbs
        : macroSummaryHide,
  },
  {
    key: 'patient_app_key_nutrients',
    label: 'Macronutrients to show in summary',
    description: 'Up to 4 nutrients can be displayed in a macronutrient summary card in the RxFood app.',
    control: { kind: 'keyNutrients' },
    visibleWhen: 'clinician_patient_allow_macro_card_toggle',
    hideInSafeMode: true,
    image: (_, ctx) => (ctx.showTotalCarbs ? macroSummaryShowTotalCarbs : macroSummaryShowNetCarbs),
  },
  {
    key: 'patient_app_show_diet_score',
    label: 'Show Mediterranean diet score in mobile app',
    control: { kind: 'boolean', onLabel: 'Show diet score', offLabel: 'Hide diet score' },
    visibleWhen: 'clinician_patient_allow_cal_carb_toggle',
    hideInSafeMode: true,
    image: (v) => (v === true || v === 'true' ? dietScoreShow : nutritionInsightsHide),
  },
  {
    key: 'patient_app_show_nutrition_data_and_insights',
    label: 'Show nutritional data and insights in mobile app',
    control: {
      kind: 'boolean',
      onLabel: 'Show nutritional insights',
      offLabel: 'Hide nutritional insights',
    },
    visibleWhen: 'clinician_patient_allow_cal_carb_toggle',
    hideInSafeMode: true,
    image: showOrHide(nutritionInsightsShow, nutritionInsightsHide),
  },
  {
    key: 'patient_app_coaching_tab',
    label: 'Show explore tab in mobile app',
    description: 'Suggest recipes, meal suggestions, and articles in the RxFood app.',
    control: { kind: 'boolean', onLabel: 'Show explore tab', offLabel: 'Hide explore tab' },
    visibleWhen: 'clinician_patient_allow_patient_app_coaching_tab_toggle',
    hideInSafeMode: true,
    image: showOrHide(coachingTabShow, coachingTabHide),
  },
  {
    key: 'patient_app_mindful_eating_survey',
    label: 'Show mindful eating survey in mobile app',
    control: {
      kind: 'boolean',
      onLabel: 'Show mindful eating survey',
      offLabel: 'Hide mindful eating survey',
    },
    visibleWhen: 'clinician_patient_allow_mindful_eating_survey_toggle',
    hideInSafeMode: false,
    image: showOrHide(mindfulEatingShow, mindfulEatingHide),
  },
];

/**
 * Flags forced when "Safe mode" is on. Mirrors v1's `safeModeFlagKeys`
 * behavior — sets the listed flags to false (or empty for the primary
 * macro) so the patient app starts in a stripped-down state.
 */
export const SAFE_MODE_FLAGS: Partial<Record<PatientFlagKey, string | boolean>> = {
  patient_app_primary_macronutrient: '',
  patient_app_primary_macronutrient_can_edit: false,
  patient_app_show_diet_score: false,
  patient_app_show_nutrition_data_and_insights: false,
  patient_app_coaching_tab: false,
  patient_app_show_macro_nutrition_card: false,
};

/**
 * Pull the patient-app flags out of a clinic's flag bag — these become
 * the defaults shown next to each control as "Clinic default ({{value}})".
 */
export const clinicDefaultsFor = (clinicFlags: Record<string, unknown> | undefined): PatientFlagsState => {
  const out: PatientFlagsState = {};
  if (!clinicFlags) return out;
  for (const k of PATIENT_FLAG_KEYS) {
    const v = clinicFlags[k];
    if (v !== undefined && v !== null) out[k] = v as never;
  }
  return out;
};

/**
 * Selectable values for the primary macronutrient dropdown. Order matches
 * v1's `primaryNutrientOptions`.
 */
export const PRIMARY_MACRO_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'protein_g', label: 'Protein' },
  { value: 'fat_g', label: 'Fat' },
  { value: 'default_carbs_g', label: 'Carbohydrate' },
  { value: 'energy_kcal', label: 'Energy (kcal)' },
  { value: 'fiber_g', label: 'Fiber' },
] as const;

/**
 * Nutrient choices for the multi-select. Same five as v1's macro summary
 * picker.
 */
export const KEY_NUTRIENT_OPTIONS = [
  { value: 'protein_g', label: 'Protein' },
  { value: 'fat_g', label: 'Fat' },
  { value: 'default_carbs_g', label: 'Carbohydrate' },
  { value: 'energy_kcal', label: 'Energy (kcal)' },
  { value: 'fiber_g', label: 'Fiber' },
] as const;

/**
 * Default `patient_app_key_nutrients` value used when the clinician
 * toggles the macro summary on without picking nutrients explicitly.
 * Mirrors the swagger example for the flag — protein/fat/carbs/energy.
 */
export const DEFAULT_KEY_NUTRIENTS_CSV = 'protein_g, fat_g, default_carbs_g, energy_kcal';
